/**
 * The canonical market list for every Location select in Orbit.
 *
 * Fetched rather than hard-coded: the list it replaced (`CLINIC_OPTIONS`, the keys
 * of a placeholder `CLINIC_PHYSICIANS` map) had drifted so far from the markets
 * bariendo-be actually resolves that they overlapped on almost nothing — the panel
 * would have offered `Greater Boston` while the form recorded `Boston`.
 *
 * Built on `useSearchResources` rather than its own fetch-and-state so it inherits
 * the MedplumClient GET cache. Both call sites can render several times per page —
 * the panel row plus one intro-call widget — and identical queries dedupe instead
 * of issuing a request each.
 *
 * Never throws. Both call sites are rows inside a patient panel, and a failed
 * market fetch must not blank the section around it: `PanelSelectRow` appends an
 * out-of-list stored value to its own data, so a rep still sees what is set and
 * only loses the ability to change it.
 */

import { isOk } from '@medplum/core';
import { useSearchResources } from '@medplum/react';
import { useEffect, useMemo } from 'react';
import { marketNames, MARKET_SEARCH_PARAMS } from '@bariendo/lib/markets';
import { reportError } from '@/services/error-reporting';

export interface MarketOptions {
  markets: string[];
  loading: boolean;
}

export function useMarketOptions(): MarketOptions {
  const [locations, loading, outcome] = useSearchResources('Location', MARKET_SEARCH_PARAMS);

  useEffect(() => {
    // Reported rather than swallowed: an empty select is indistinguishable from a
    // project that genuinely has no markets, which is exactly how this would hide.
    if (outcome && !isOk(outcome)) {
      reportError(new Error('Market registry search failed'), {
        component: 'useMarketOptions',
        extra: { outcome },
      });
    }
  }, [outcome]);

  const markets = useMemo(() => marketNames(locations ?? []), [locations]);

  return { markets, loading };
}
